import { useContext, useEffect, useMemo, useState } from 'react';
import { shallowEqual } from 'react-redux';

import { Box, Button, Card, Text } from '@zillow/constellation-10';
import { TourSchedulingUpsell } from '@zg-rentals/touring';
import { useZCalendarWebApiHandler_getTouringTreatmentForZuidAndListing } from '@zg-rentals/ts-z-calendar-client-react-query';
import '@zg-rentals/touring/src/styles/tourCalendar.scss';
import { LoadingButton, LoadingText } from '@zg-rentals/loading-components';

import { RMW_PATH } from '../../pathsManifest';
import { RMWContext } from '../../providers/RMWContext';
import { DashboardContext } from '../../containers/DashboardPage/context';
import { LoadingMapContext } from '../../providers/LoadingMapContext/context';

import { useSelector } from '../../selectors/useSelector';

import browserUtils from '../../utils/browserUtils';
import stringHelper from '../../utils/stringHelper';
import accessUtils from '../../utils/accessUtils';

import { useGetInterestedRenters } from '../../hooks/queries/interestedRenters';
import { updateLoadingState } from '../../providers/LoadingMapContext/actions';

import { VERTICAL_DISPLAY_TYPE } from '../../constants/PropertiesConstants';
import TouringPropertyDashboardCSEvents from '../../model/ClickstreamEvent/Touring/TouringPropertyDashboardCSEvents';

import ConnectedModuleCard from '../ConnectedModuleCard';
import ErrorMessagesAlert from '../ErrorMessagesAlert';
import LeadDetails from './components/LeadDetails';
import NoInterestedRenters from './components/NoInterestedRenters';
import SimpleCard from '../SimpleCard';

import { NoUnderlineAnchor } from '../../styles';

import type { Lead } from './components/LeadDetails';

const TITLE = 'Find a renter';

const MOCK_LEAD = {
  conversationId: '',
  latestContact: {
    isPhoneLead: false,
    isRead: true,
    messageDateMs: '',
    messageText: '',
    notRepliedTo: false,
  },
  renterInfo: {
    renterPhoneNumber: '',
    renterName: '',
  },
  status: {
    isConversationBlockedByZillow: false,
  },
  statusLabel: {
    linkId: '',
    text: '',
  },
};

type Props = {
  limit?: number;
};

const InterestedRenters = ({ limit = 3 }: Props) => {
  const { currentProperty, user, appConfig } = useSelector(
    (state) => ({
      currentProperty: state.properties.currentProperty,
      user: state.user,
      appConfig: state.app.appConfig,
    }),
    shallowEqual,
  );

  const canAccessPostingPathTourDuration = accessUtils.canAccessPostingPathTourDuration({ appConfig });

  const { listingAlias, street, isFeed } = currentProperty;

  const {
    data,
    isError: hasError,
    isPending: isGetInterestedRentersLoading,
  } = useGetInterestedRenters({
    listingAlias,
    clientTimeZone: browserUtils.timezone(),
    limit,
  });
  const { data: touringTreatment, isLoading: isGetTouringTreatmentLoading } =
    useZCalendarWebApiHandler_getTouringTreatmentForZuidAndListing(
      {
        listingAlias,
      },
      { enabled: Boolean(listingAlias) },
    );
  const {
    // @ts-expect-error
    dashboardState: { activePath },
  } = useContext(DashboardContext);
  const { paths } = useContext(RMWContext);
  const {
    loadingByComponent: { InterestedRenters: isInterestedRentersLoading },
    reducerDispatch,
  } = useContext(LoadingMapContext);

  useEffect(() => {
    updateLoadingState({
      reducerDispatch,
      data: {
        interestedRenters: isGetInterestedRentersLoading,
        touringTreatment: isGetTouringTreatmentLoading,
      },
    });
  }, [isGetInterestedRentersLoading, isGetTouringTreatmentLoading]);

  // we want all error messages for interested renters to be this message
  const [partialErrorMessages, setPartialErrorMessages] = useState([
    { text: 'An error occurred, please try refreshing the page.' },
  ]);

  const inquiriesPath = `${paths.base}/lead-management/${listingAlias}`;
  const isActivePathOverview = activePath === RMW_PATH.DASHBOARD_OVERVIEW;

  const { leads, totalLeadCount = 0 } = data || {};

  const leadListLength = leads?.length || 0;
  const remainingTotalLeadCount = totalLeadCount - leadListLength;

  const displayType = isActivePathOverview ? VERTICAL_DISPLAY_TYPE.SIMPLE : VERTICAL_DISPLAY_TYPE.FULL;

  const renderCTA = useMemo(() => {
    const ctaLabel = displayType === VERTICAL_DISPLAY_TYPE.SIMPLE ? 'Details' : 'View all';

    if (isInterestedRentersLoading) {
      return (
        <LoadingButton size="sm" isLoading={isInterestedRentersLoading} loadingWidth="7rem">
          {ctaLabel}
        </LoadingButton>
      );
    }

    if (leadListLength === 0) {
      return null;
    }

    return (
      <>
        {displayType === VERTICAL_DISPLAY_TYPE.SIMPLE ? (
          <NoUnderlineAnchor href={inquiriesPath} target="_self" onClick={() => {}}>
            {ctaLabel}
          </NoUnderlineAnchor>
        ) : (
          <Button asChild tone="neutral" emphasis="outlined" size="sm" data-testid="view-all-applications-cta">
            <a target="_self" href={inquiriesPath} onClick={() => {}}>
              {ctaLabel}
            </a>
          </Button>
        )}
      </>
    );
  }, [isInterestedRentersLoading, displayType, leadListLength]);

  const renderLeadDetailsList = useMemo(() => {
    const leadsToRender = isInterestedRentersLoading ? Array(2).fill(MOCK_LEAD) : data?.leads;

    if (!leadsToRender) {
      return null;
    }

    return leadsToRender.map((lead: Lead, i: number) => {
      return (
        <Box key={i}>
          <LeadDetails lead={lead} includeDivider={i !== (data?.leads?.length || 0) - 1} />
        </Box>
      );
    });
  }, [isInterestedRentersLoading, data?.leads]);

  const renderLeadListWithMoreLeadsCTA = useMemo(() => {
    if (isInterestedRentersLoading && displayType === VERTICAL_DISPLAY_TYPE.SIMPLE) {
      return <SimpleCard isLoading={true} />;
    }

    return (
      <>
        {renderLeadDetailsList}

        {remainingTotalLeadCount > 0 && (
          <>
            <Box css={{ marginBottom: '600' }} />

            <div data-testid="MoreLeadsContainer">
              <Text
                textStyle="body-sm"
                asChild
                css={{ color: 'text.subtle', _hover: { color: 'text.action.hero.default' } }}
              >
                <NoUnderlineAnchor href={inquiriesPath} target="_blank">
                  {`+ ${remainingTotalLeadCount} more ${stringHelper.pluralizeWord(
                    remainingTotalLeadCount,
                    'lead',
                    'leads',
                  )}`}
                </NoUnderlineAnchor>
              </Text>
            </div>
          </>
        )}
      </>
    );
  }, [isInterestedRentersLoading, leadListLength, displayType, remainingTotalLeadCount]);

  const renderFullType = () => {
    return (
      <>
        {leadListLength > 0 || isInterestedRentersLoading ? (
          <ConnectedModuleCard
            data-testid="InterestedRentersFull"
            title={TITLE}
            ctaComponent={renderCTA}
            ctaFontColor="text.subtle"
            ctaFontType="body-sm"
            justifyHeadingContent="center"
          >
            {renderLeadListWithMoreLeadsCTA}
          </ConnectedModuleCard>
        ) : (
          <Box>
            <NoInterestedRenters />
          </Box>
        )}
      </>
    );
  };

  const handleTourSchedulingUpsellClick = () => {
    TouringPropertyDashboardCSEvents.TourSchedulingUpsellSetTourAvailabilityCta({ currentProperty, user });
    return;
  };

  const renderSimpleType = () => {
    return (
      <ConnectedModuleCard
        data-testid="InterestedRentersSimple"
        title={TITLE}
        ctaComponent={renderCTA}
        ctaFontColor="text.subtle"
        ctaFontType="body-sm"
        justifyHeadingContent="center"
      >
        {touringTreatment &&
          !touringTreatment.isTouringEnabledForListing &&
          !touringTreatment.hasAvailability &&
          !isInterestedRentersLoading &&
          !isFeed && (
            <Box css={{ marginBottom: '400' }}>
              <TourSchedulingUpsell
                listingAlias={listingAlias}
                modalHeaderText={street}
                onClick={handleTourSchedulingUpsellClick}
                clickStreamMetadata={{ user, currentProperty }}
                eventId={TouringPropertyDashboardCSEvents.TourSchedulingUpsellSetTourAvailabilityCta.data_event_id}
                canAccessPostingPathTourDuration={canAccessPostingPathTourDuration}
              />
            </Box>
          )}

        {leadListLength > 0 || isInterestedRentersLoading ? (
          renderLeadListWithMoreLeadsCTA
        ) : (
          <Card data-testid="EmptyInterestedRentersCardSimple" tone="neutral" elevated={false}>
            <LoadingText
              textStyle="body-bold"
              color="textDark"
              isLoading={isInterestedRentersLoading}
              loadingWidth="100%"
            >
              No leads yet
            </LoadingText>
            <Box css={{ lineHeight: '0px' }}>
              <LoadingText
                textStyle="body-sm"
                color="textDark"
                isLoading={isInterestedRentersLoading}
                loadingWidth="75%"
              >
                Messages from interested renters will appear here.
              </LoadingText>
            </Box>
          </Card>
        )}
      </ConnectedModuleCard>
    );
  };

  if (hasError) {
    return (
      <ConnectedModuleCard title={TITLE}>
        <ErrorMessagesAlert appearance="warning" errorMessages={partialErrorMessages} />
      </ConnectedModuleCard>
    );
  }

  return <div data-testid="InterestedRenters">{isActivePathOverview ? renderSimpleType() : renderFullType()}</div>;
};

export default InterestedRenters;
